// src/component/AdminHome.js
// ★ カテゴリ別セクション分け版
import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import VideoLibraryRoundedIcon from "@mui/icons-material/VideoLibraryRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import MusicNoteRoundedIcon from "@mui/icons-material/MusicNoteRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import MonitorHeartRoundedIcon from "@mui/icons-material/MonitorHeartRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";

import VpnKeyRoundedIcon from "@mui/icons-material/VpnKeyRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";
import MapRoundedIcon from "@mui/icons-material/MapRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";

import { AppThemeContext } from "./AppShell";
import MilitaryTechRoundedIcon from "@mui/icons-material/MilitaryTechRounded";
import { ManageSearchRounded } from "@mui/icons-material";
import LocalActivityRoundedIcon from "@mui/icons-material/LocalActivityRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import PreviewRoundedIcon from "@mui/icons-material/PreviewRounded";
import ForwardToInboxRoundedIcon from "@mui/icons-material/ForwardToInboxRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import GroupAddRoundedIcon from "@mui/icons-material/GroupAddRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";

export default function AdminHome() {
  const navigate = useNavigate();
  const theme = React.useContext(AppThemeContext) || {};

  const accent = theme.railActive ?? "#5A35FF";
  const border = theme.bottomBorder ?? "rgba(0,0,0,0.08)";
  const surface = theme.surfaceBg ?? "#FFF";
  const textBase = theme.railText ?? "rgba(40,40,60,0.9)";

  const sections = [
    {
      key: "preview-ui",
      title: "表示プレビュー / UI制御",
      cards: [
        {
          key: "toppage-preview",
          title: "擬似トップページ",
          description: "ユーザーに表示されるトップページをプレビューします。",
          icon: <PreviewRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/toppage-preview"),
        },
        {
          key: "ui-visibility",
          title: "表示関連",
          description: "各機能の表示条件・解放タイミング・案内文を管理します。",
          icon: <VisibilityRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/ui-visibility"),
        },
        {
          key: "community-feed-visibility",
          title: "コミュニティ表示制御",
          description:
            "トップページの投稿フィードを表示するユーザー条件を設定します。",
          icon: <VisibilityRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/community-feed-visibility"),
        },
        {
          key: "ribbon-bubble",
          title: "リボン吹き出し",
          description:
            "リボンボタンの吹き出しメッセージの表示ON/OFFとテキストを設定します。",
          icon: <MilitaryTechRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/ribbon-bubble"),
        },
      ],
    },
    {
      key: "contents",
      title: "コンテンツ",
      cards: [
        {
          key: "videos",
          title: "動画管理",
          description: "動画の登録・編集・公開範囲を設定します。",
          icon: <VideoLibraryRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/videos"),
        },
        {
          key: "music",
          title: "音楽管理",
          description: "季節ごとのBGMを登録・管理します。",
          icon: <MusicNoteRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/music"),
        },
        {
          key: "importantAnnouncement",
          title: "基本情報",
          description: "ユーザに向けお知らせを発信しいます。固定しない。",
          icon: <NewspaperIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/importantAnnouncement"),
        },
        {
          key: "daily-posts",
          title: "最新情報",
          description: "ユーザに向けお知らせを発信しいます。固定する。",
          icon: <BookmarkRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/daily-posts"),
        },
      ],
    },
    {
      key: "users",
      title: "ユーザー / 権限",
      cards: [
        {
          key: "users-list",
          title: "ユーザー管理",
          description: "ユーザーの検索、権限、基本情報を管理します。",
          icon: <PeopleAltRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/users"),
        },
        {
          key: "user-attributes",
          title: "ユーザ属性",
          description:
            "支払フェーズ別のユーザを抽出し、都道府県別人数をグラフと数字で確認します。",
          icon: <ManageSearchRounded sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/user-attributes"),
        },
        {
          key: "grant-by-uid",
          title: "UIDから権限追加",
          description:
            "UIDを指定して権限（isAdmin / paymentPhase 等）を付与します。",
          icon: <PersonAddAlt1RoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/grant-by-uid"),
        },
        {
          key: "sekaimura-token-register",
          title: "セカイムラUID登録",
          description:
            "スプレッドシート等から貼り付けたUIDを sekaimuraUserToken に未登録分だけ追加します。",
          icon: <VpnKeyRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/sekaimura-token-register"),
        },
      ],
    },
    {
      key: "promo",
      title: "お知らせ・販促",
      cards: [
        {
          key: "mm2026-dialog",
          title: "MM2026販促ダイアログ",
          description: "2026参加のご案内ダイアログの表示／非表示を切り替えます。",
          icon: <CampaignRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/mm2026-dialog"),
        },
        {
          key: "promo-banner",
          title: "サイト上部プッシュ通知",
          description:
            "サイト上部に常時表示する案内バナーと、日次1回のダイアログ（文言・画像・リンク・終了日時）を編集します。",
          icon: <CampaignRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/promo-banner"),
        },
      ],
    },
    {
      key: "mm2026-payment",
      title: "MM2026 支払い",
      cards: [
        {
          key: "payment2026",
          title: "支払い管理2026",
          description:
            " MM2026（第4期）の支払いフェーズ（早割・通常・駆け込みなど）の価格と販売期間を管理します。",
          icon: <PaidRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/paymentAdmin2026"),
        },
        {
          key: "bankTransfer2026",
          title: "銀行振込管理2026",
          description: "銀行振込を行ったユーザの4期（2026）権限を付与します。",
          icon: <PaidRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/bankTransferList"),
        },
        {
          key: "paymentStatus2026",
          title: "2026支払状況",
          description:
            "第4期（MM2026）の参加者・支払い方法・参加日を一覧とグラフで確認します。",
          icon: <PaidRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/paymentStatus2026"),
        },
      ],
    },
    {
      key: "mm2026-group",
      title: "MM2026 グループ活動",
      cards: [
        {
          key: "group-activity-2026",
          title: "2026グループ",
          description:
            "4期ユーザー向け「2026年グループ活動」参加可否ダイアログの表示・文言を管理します。（現在は管理者のみテスト表示中）",
          icon: <GroupsRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/group-activity-2026"),
        },
        {
          key: "group-activity-2026-list",
          title: "2026グループ参加状況",
          description:
            "4期支払い済みユーザーの「参加する／参加しない／未回答」を、都道府県・州・国別のグラフで確認します。",
          icon: <GroupsRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/group-activity-2026-list"),
        },
        {
          key: "group-activity-2026-bulk-join",
          title: "2026グループ 一斉参加登録",
          description:
            "ユーザー uid を改行区切りで貼り付け、まとめて「参加する」状態にする事務局代理ツール。無法松コホートなど締切後の駆け込み登録に使います。",
          icon: <GroupAddRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/group-activity-2026-bulk-join"),
        },
        {
          key: "group-allocation-2026",
          title: "2026グループ分け",
          description:
            "「参加する」と回答した4期ユーザーを地域別に4〜6人のグループへ編成します。通常コホート／無法松コホート対応。",
          icon: <GroupsRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/group-allocation-2026"),
        },
        {
          key: "group-allocation-2026-watch",
          title: "2026グループ確認",
          description:
            "グループ編成の進行をリアルタイムで監視する閲覧専用画面。変更があったグループは30秒間ハイライト表示されます。",
          icon: <GroupsRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/group-allocation-2026-watch"),
        },
        {
          key: "prefecture-city-mismatch",
          title: "県・市区町村チグハグユーザー",
          description:
            "都道府県と市区町村の組み合わせが不整合なユーザーを洗い出します（例：北海道なのに渋谷区）。全件スキャンの結果をキャッシュします。",
          icon: <ManageSearchRounded sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/prefecture-city-mismatch"),
        },
        {
          key: "guides-2026",
          title: "2026年 ガイド",
          description:
            "節気ごとのガイド（タイトル・本文・日付）を管理します。グループ画面のガイドタブに反映されます。",
          icon: <GroupsRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/guides-2026"),
        },
        {
          key: "group-reports-2026",
          title: "2026グループ通報管理",
          description:
            "ユーザーが仲間タブから報告した「グループ活動未参加」など通報内容を事務局が確認・対応します。対応履歴の追加・編集・完了管理も可能。",
          icon: <ReportProblemRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/group-reports-2026"),
        },
        {
          key: "group-chats-2026-stats",
          title: "2026グループチャット集計",
          description:
            "各グループのチャット活動量を集計・閲覧します（メッセージ数 / いいね数 / 画像数 / ユーザー別）。手動で集計を再実行できます。",
          icon: <QueryStatsRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/group-chats-2026-stats"),
        },
        {
          key: "group-chat-2026-migrate",
          title: "2026グループチャット移行",
          description:
            "旧グループIDのチャット履歴を新グループIDへコピーします（グループ作り直しでチャットが消えた時の復旧用）。元データは残したまま、いいね・返信・画像もそのまま引き継ぎます。",
          icon: <SwapHorizRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/group-chat-2026-migrate"),
        },
      ],
    },
    {
      key: "music-monitor",
      title: "音楽 監視 / ログ",
      cards: [
        {
          key: "musicPlaybackLogs",
          title: "音楽再生ログ監視",
          description: "音源の再生エラー・フリーズ発生状況を確認します。",
          icon: <WarningAmberRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/music-playback-logs"),
        },
        {
          key: "musicMonitoring",
          title: "音楽監視（設定）",
          description: "パフォーマンス監視・再生監視のON/OFFを切り替えます。",
          icon: <MonitorHeartRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/music-monitoring"),
        },
        {
          key: "musicListeningControl",
          title: "音楽 Listening 制御",
          description:
            "Listening status の更新間隔・更新時間（バースト）を設定します。",
          icon: <MonitorHeartRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/music-listening-control"),
        },
        {
          key: "music-playback-errors",
          title: "音楽再生エラー",
          description:
            "音楽再生に失敗した際のエラーログの取得ON/OFFと一覧を確認します。",
          icon: <ErrorOutlineRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/music-playback-errors"),
        },
      ],
    },
    {
      key: "otohikari-map",
      title: "OTOHIKARIマップ",
      cards: [
        {
          key: "otohikari-settings",
          title: "OTOHIKARIマップ設定",
          description:
            "トップページのOTOHIKARIマップに表示される TARGET SCHUMANN 周波数（Hz）を設定します。",
          icon: <PublicRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/otohikari"),
        },
      ],
    },
    {
      key: "system",
      title: "システム",
      cards: [
        {
          key: "currentBuildControl",
          title: "BuildID 制御",
          description:
            "強制アップデートの基準（required/minSupported/rollout/message）を管理します。",
          icon: <TuneRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/current-build"),
        },
        {
          key: "day-boundary",
          title: "日付切り替え時刻",
          description:
            "音楽再生カウント（TODAY等）の日付が切り替わるJST時刻を設定します。",
          icon: <AccessTimeRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/day-boundary"),
        },
      ],
    },
    {
      key: "others",
      title: "その他",
      cards: [
        {
          key: "events",
          title: "イベント",
          description: "イベントの一覧を確認・管理します。",
          icon: <EventNoteRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/events"),
        },
        {
          key: "village-map",
          title: "村マップ（Google）",
          description: "Google Maps を使った村マップを表示します。",
          icon: <MapRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/village-map"),
        },
        {
          key: "village-map-free",
          title: "村マップ（フリー）",
          description: "Leaflet を使った村マップを表示します。",
          icon: <MapRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/village-map-free"),
        },
        {
          key: "coupon",
          title: "クーポン管理",
          description: "クーポンの作成・編集・配布状況を管理します。",
          icon: <LocalActivityRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/coupon"),
        },
        {
          key: "cotozute",
          title: "cotozute関連",
          description: "cotozuteに関する管理機能です。",
          icon: <ForwardToInboxRoundedIcon sx={{ fontSize: 30, color: accent }} />,
          onClick: () => navigate("/admin/cotozute"),
        },
      ],
    },
  ];

  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="overline"
          sx={{
            letterSpacing: "0.18em",
            fontSize: 11,
            opacity: 0.8,
            color: textBase,
          }}
        >
          ADMIN
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            letterSpacing: "0.03em",
            color: textBase,
          }}
        >
          管理者
        </Typography>

        <Typography variant="body2" sx={{ mt: 0.5, color: "rgba(0,0,0,0.6)" }}>
          各種データ管理画面にアクセスできます。
        </Typography>
      </Box>

      <Stack spacing={3}>
        {sections.map((section) => (
          <Box
            key={section.key}
            sx={{
              border: `1px solid ${border}`,
              borderRadius: 3,
              p: { xs: 2, sm: 2.5 },
              bgcolor: "rgba(255,255,255,0.4)",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ mb: 2 }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                  color: textBase,
                  textTransform: "uppercase",
                  fontSize: 12,
                  whiteSpace: "nowrap",
                }}
              >
                {section.title}
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  height: 1,
                  background: `linear-gradient(90deg, ${accent}33, transparent)`,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(0,0,0,0.45)",
                  fontWeight: 600,
                  fontSize: 11,
                }}
              >
                {section.cards.length}
              </Typography>
            </Stack>

            <Grid container spacing={2.5}>
              {section.cards.map((card) => (
                <Grid item key={card.key} xs={12} sm={6} md={4}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      borderRadius: 3,
                      border: `1px solid ${border}`,
                      bgcolor: surface,
                      boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
                    }}
                  >
                    <CardActionArea
                      onClick={card.onClick}
                      sx={{
                        height: "100%",
                        p: 2.2,
                        "&:hover": {
                          boxShadow: "0 16px 40px rgba(0,0,0,0.12)",
                          transform: "translateY(-2px)",
                        },
                        transition: "all .18s ease-out",
                      }}
                    >
                      <CardContent sx={{ p: 0 }}>
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="center"
                          sx={{ mb: 1.5 }}
                        >
                          <Box
                            sx={{
                              width: 52,
                              height: 52,
                              borderRadius: 3,
                              background:
                                "radial-gradient(circle at 20% 20%, #fff, rgba(255,255,255,0))," +
                                `linear-gradient(135deg, ${accent}22, ${accent}11)`,
                              boxShadow:
                                "0 10px 24px rgba(0,0,0,0.18), inset 0 0 0 1px rgba(255,255,255,0.8)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {card.icon}
                          </Box>

                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 700,
                                color: textBase,
                                mb: 0.3,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {card.title}
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: 12,
                                color: "rgba(0,0,0,0.6)",
                                lineHeight: 1.6,
                              }}
                            >
                              {card.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
